import { useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useSiteData } from "@/context/SiteDataContext";
import { API_BASE } from "@/config/api";
import { useT } from "@/lib/useSiteText";

const PREVIEW_KEY = "borman_preview_token";

// El permiso de vista previa vive solo en la pestaña (sessionStorage): una pestaña
// nueva sin el link ve el aviso de "en construccion", igual que el publico.
function readPreviewToken(): string {
  const fromUrl = new URLSearchParams(window.location.search).get("preview") ?? "";
  try {
    // version anterior: guardaba el permiso en localStorage y valia para todo el navegador
    localStorage.removeItem(PREVIEW_KEY);
    if (fromUrl) {
      sessionStorage.setItem(PREVIEW_KEY, fromUrl);
      return fromUrl;
    }
    return sessionStorage.getItem(PREVIEW_KEY) ?? "";
  } catch {
    return fromUrl;
  }
}

function forgetPreviewToken() {
  try {
    sessionStorage.removeItem(PREVIEW_KEY);
  } catch {
    // sin acceso al almacenamiento: nada que olvidar
  }
}

function exitPreview() {
  forgetPreviewToken();
  window.location.assign(window.location.pathname);
}

function MaintenanceScreen({ brand }: { brand: string }) {
  const t = useT();
  const texto = t("mantenimiento.texto");
  return (
    <div className="min-h-screen bg-background-100 flex items-center justify-center px-4">
      <div className="w-full max-w-md text-center rounded-lg bg-background-50 border border-background-200 p-8 md:p-10">
        <span className="mx-auto w-14 h-14 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center">
          <i className="ri-tools-line text-2xl" />
        </span>
        <p className="mt-5 text-sm font-semibold text-accent-600">{brand}</p>
        <h1 className="mt-2 font-heading font-bold text-2xl text-foreground-950">{t("mantenimiento.titulo")}</h1>
        {texto && <p className="mt-3 text-foreground-600">{texto}</p>}
      </div>
    </div>
  );
}

export default function MaintenanceGate({ children }: { children: ReactNode }) {
  const { data } = useSiteData();
  const { pathname } = useLocation();
  const [token] = useState(readPreviewToken);
  const [previewOk, setPreviewOk] = useState<boolean | null>(token ? null : false);

  const inactive = data.siteSettings.site_active === false;
  const isAdmin = pathname === "/admin" || pathname.startsWith("/admin/");

  useEffect(() => {
    if (!inactive || !token) return;
    let cancelled = false;
    fetch(`${API_BASE}/site-settings/preview/verify`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    })
      .then((res) => res.json())
      .then((json) => {
        if (cancelled) return;
        const ok = json?.valid === true;
        if (!ok) forgetPreviewToken();
        setPreviewOk(ok);
      })
      .catch(() => {
        if (!cancelled) setPreviewOk(false);
      });
    return () => {
      cancelled = true;
    };
  }, [inactive, token]);

  if (!inactive || isAdmin) return <>{children}</>;

  if (previewOk === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-primary-200 border-t-primary-500 animate-spin" />
      </div>
    );
  }

  if (!previewOk) return <MaintenanceScreen brand={data.siteSettings.brand} />;

  return (
    <>
      {children}
      <div className="fixed bottom-4 left-4 z-40 flex items-center gap-2 rounded-full bg-accent-500 pl-3 pr-1.5 py-1.5 text-xs font-semibold text-background-50 shadow-lg">
        Vista previa: el sitio está desactivado para el público
        <button
          type="button"
          onClick={exitPreview}
          className="rounded-full bg-background-50/25 px-2.5 py-0.5 hover:bg-background-50/40 transition cursor-pointer"
        >
          Salir
        </button>
      </div>
    </>
  );
}
